import { Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader } from '~/components/ui/card';
import type { CoverageCategoryRow } from '~/features/catalog-operations/types/catalog-operations.types';

import { CoverageBar } from '../coverage-bar';

interface Props {
  rows: CoverageCategoryRow[];
}

/** Coverage by product category — how much of each resolved category came back
 *  enriched. The percentage denominator is the category's *attempted* products
 *  (failures included), which is why it can sit below 100 on a run that looked
 *  clean. */
export function CoverageCategorySignalList({ rows }: Props) {
  const { t } = useTranslation('catalog-operations');

  return (
    <Card data-testid="batch-detail-category-signal-card">
      <CardHeader className="flex flex-row items-center gap-4 p-6">
        <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <Layers className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-0.5">
          <h2 data-testid="batch-detail-category-signal-title" className="text-xl font-medium">
            {t('coverageSignals.byCategory.title')}
          </h2>
          <p className="text-muted-foreground text-sm font-light">
            {t('coverageSignals.byCategory.subtitle')}
          </p>
        </div>
      </CardHeader>

      <CardContent className="bg-primary/4 mr-6 mb-6 ml-6 max-h-65 overflow-y-auto rounded-xl p-3">
        {rows.length === 0 ? (
          <p
            data-testid="batch-detail-category-signal-empty"
            className="text-muted-foreground px-3 py-6 text-center text-sm"
          >
            {t('coverageSignals.empty')}
          </p>
        ) : (
          <ul data-testid="batch-detail-category-signal-list">
            {rows.map((row) => (
              <li
                key={row.label}
                data-testid={`batch-detail-category-signal-item-${row.label}`}
                className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_auto_auto] items-center gap-4 px-3 py-3"
              >
                <span className="text-sm capitalize">{row.label}</span>
                <CoverageBar percent={row.percent} />
                <span className="text-muted-foreground w-16 text-right text-xs tabular-nums">
                  {t('coverageSignals.byCategory.enrichedCount', { count: row.enriched })}
                </span>
                <span
                  data-testid={`batch-detail-category-signal-item-value-${row.label}`}
                  className="w-14 text-right text-sm font-bold tabular-nums"
                >
                  {row.percent}%
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
